import { VERTICAL_BADGE, VERTICAL_OPTIONS, verticalLabel, type Vertical } from '@/lib/vertical';
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { DataTable } from '@/components/DataTable';
import { Modal } from '@/components/Modal';
import { Button } from '@/components/Button';
import { useCanEdit } from '@/components/EditOnly';
import { getApps, getAppOwners, createApp, updateApp, deleteApp, getAppCampaigns, assignCampaigns, unassignCampaign, refreshStoreVersions, refreshAppReview, getAppGeos, clearOfferHold } from '@/api/apps';
import { getCampaigns } from '@/api/campaigns';
import { Select } from '@/components/Select';
import { MediaUpload } from '@/components/MediaUpload';
import { AppBanPanel } from '@/components/AppBanPanel';
import { CountryMultiSelect } from '@/components/CountryMultiSelect';
import { uploadMedia } from '@/api/ads';
import type { App, Campaign, AppCampaign, CreateAppInput, OfferHold, UpdateAppInput } from '@/api/types';
import { COUNTRIES, COUNTRY_MAP } from '@/lib/countries';
import { formatDate, formatDateTime } from '@/lib/datetime';
import { useAuthStore } from '@/store/auth';
import { GoogleAdsPublishModal } from '@/components/GoogleAdsPublishModal';
import { AppPackageModal } from '@/components/apps/AppPackageModal';
import { AppCommentsModal } from '@/components/apps/AppCommentsModal';
import { AppFirebaseModal } from '@/components/apps/AppFirebaseModal';
import { PlayCountriesDropdown, playCountries } from '@/components/apps/PlayCountriesDropdown';

const COUNTRY_FLAG: Record<string, string> = COUNTRIES.reduce<Record<string, string>>((acc, c) => {
  acc[c.code] = c.flag;
  return acc;
}, {});

// Гео, где антимодерка выключена (apps.antimoderation_off_countries, "BR,MX").
function antimodCountries(app: App): string[] {
  return (app.antimoderation_off_countries || '').split(',').map((c) => c.trim()).filter(Boolean);
}

// Гео, куда нельзя лить (apps.forbidden_countries, "AR,UY") — бренд там недоступен.
function forbiddenCountries(app: App | null | undefined): string[] {
  return (app?.forbidden_countries || '').split(',').map((c) => c.trim()).filter(Boolean);
}

// Бейдж в строке списка: до трёх кодов, остальное счётчиком.
function antimodGeoLabel(codes: string[]): string {
  return codes.length > 3 ? `${codes.slice(0, 3).join(', ')} +${codes.length - 3}` : codes.join(', ');
}

// Пауза force offer после выхода в стор (worker lib/offer-hold.ts): что держит и сколько осталось.
const OFFER_HOLD_WHY: Record<OfferHold['kind'], string> = {
  unreleased: 'Приложение ещё не вышло в стор — оффер не выдаётся',
  release: 'Пауза после релиза',
  meta: 'Пауза после выхода новой меты',
  build: 'Пауза после выхода нового билда',
  spike: 'Всплеск новых устройств — похоже на проверку стора',
  ramp: 'Постепенное включение после релиза',
};

function holdLeft(until: string): string {
  const min = Math.max(1, Math.round((Date.parse(until) - Date.now()) / 60_000));
  if (min < 60) return `${min}m`;
  const h = Math.round(min / 60);
  return h >= 48 ? `${Math.floor(h / 24)}d ${h % 24}h` : `${h}h`;
}

function offerHoldBadge(hold: OfferHold): { text: string; title: string } {
  const why = OFFER_HOLD_WHY[hold.kind];
  if (!hold.until) return { text: 'Not released', title: why };
  const at = formatDateTime(hold.until);
  if (hold.kind === 'ramp') {
    const pct = Math.floor((hold.share ?? 0) * 100);
    return { text: `Ramp ${pct}%`, title: `${why}: оффер видят ${pct}% устройств, все — с ${at}` };
  }
  return { text: `${holdLeft(hold.until)} left`, title: `${why}. Оффер включится ${at}` };
}

function reviewBadge(state: string | null, since: string | null): { text: string; cls: string } | null {
  if (!state) return null;
  const green = 'bg-success-soft text-success';
  const yellow = 'bg-warning-soft text-warning';
  const red = 'bg-danger-soft text-danger';
  const grey = 'bg-surface-muted text-content-subtle';
  switch (state) {
    case 'IN_REVIEW': {
      let suffix = '';
      if (since) {
        const h = Math.max(1, Math.round((Date.now() - Date.parse(since)) / 3_600_000));
        suffix = ` · ${h}h`;
      }
      return { text: `In review${suffix}`, cls: yellow };
    }
    case 'PUBLISHED': return { text: 'Published', cls: green };
    case 'APPROVED_NOT_PUBLISHED': return { text: 'Approved', cls: green };
    case 'NOT_APPROVED': return { text: 'Rejected', cls: red };
    case 'DRAFT': return { text: 'Draft', cls: grey };
    case 'NOT_SENT_FOR_REVIEW': return { text: 'Not submitted', cls: grey };
    default: return null;
  }
}

type ModalMode = 'create' | 'edit' | 'delete' | 'campaigns' | 'geos' | null;

interface FormData {
  owner_admin_id: string;
  name: string;
  platform: 'ios' | 'android';
  bundle_id: string;
  store_url: string;
  force_offer_url: string;
  force_offer_enabled: boolean;
  skip_version_check: boolean;
  antimoderation_off: boolean;
  antimoderation_off_countries: string[];
  forbidden_countries: string[];
  external_account: boolean;
  icon_url: string;
  description: string;
  release_notes_keywords: string;
  vertical: '' | Vertical;
  status?: 'active' | 'paused';
  pause_reason: string;
  force_offer_off_reason: string;
  in_work?: boolean;
}

const initialFormData: FormData = {
  owner_admin_id: '',
  name: '',
  platform: 'android',
  bundle_id: '',
  store_url: '',
  force_offer_url: '',
  force_offer_enabled: false,
  in_work: false,
  skip_version_check: false,
  antimoderation_off: false,
  antimoderation_off_countries: [],
  forbidden_countries: [],
  external_account: false,
  icon_url: '',
  description: '',
  release_notes_keywords: '',
  vertical: '',
  pause_reason: '',
  force_offer_off_reason: '',
};

const APPS_PAGE_SIZE = 20;

// Copy to clipboard helper. The async Clipboard API is unavailable outside a
// secure context and Chrome also rejects writeText() when the document is not
// focused, so fall back to execCommand and, failing that, show the text so the
// click never ends in silence.
async function copyToClipboard(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }
    throw new Error('clipboard unavailable');
  } catch {
    try {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.top = '-9999px';
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(ta);
      if (ok) return true;
    } catch { /* fall through to the prompt below */ }
    window.prompt('Copy:', text);
    return false;
  }
}

export function Apps() {
  const queryClient = useQueryClient();
  const account = useAuthStore((s) => s.admin);
  const canAsoMarket = useAuthStore((s) => s.hasSection('aso-market'));
  const canEdit = useCanEdit() && account?.user_type !== 'bdm';
  const isTechnical = account?.role === 'super_admin' || !!account?.addons?.includes('developer');
  const [modalMode, setModalMode] = useState<ModalMode>(null);
  // The store package (texts per locale + screenshots) opened from a row.
  const [packageApp, setPackageApp] = useState<App | null>(null);
  // Comments / Firebase modals keep the id and read the row from the live
  // list, so a refetch (new comment, provisioning step) shows up in them.
  const [commentsAppId, setCommentsAppId] = useState<string | null>(null);
  const [firebaseAppId, setFirebaseAppId] = useState<string | null>(null);
  const [selectedApp, setSelectedApp] = useState<App | null>(null);
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [formError, setFormError] = useState('');
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [selectedCampaignId, setSelectedCampaignId] = useState('');
  const [googleAdsOpen, setGoogleAdsOpen] = useState(false);

  // Filters
  const [searchInput, setSearchInput] = useState('');
  const [searchFilter, setSearchFilter] = useState('');
  const [platformFilter, setPlatformFilter] = useState<'' | 'ios' | 'android'>('');
  const [verticalFilter, setVerticalFilter] = useState<'' | 'none' | Vertical>('');
  const [statusFilter, setStatusFilter] = useState<'' | 'active' | 'paused' | 'banned' | 'in_store'>('');
  const [forceOfferFilter, setForceOfferFilter] = useState<'' | 'on' | 'off'>('');
  const [versionFilter, setVersionFilter] = useState<'' | 'with' | 'without'>('');
  const [skipVersionFilter, setSkipVersionFilter] = useState<'' | 'on' | 'off'>('');
  const [antimodFilter, setAntimodFilter] = useState<'' | 'on' | 'off' | 'geo'>('');
  const [accountFilter, setAccountFilter] = useState<'' | 'ours' | 'external'>('');
  // '' all, 'none' apps never built via CI, otherwise a build_repos.id as string.
  const [repoFilter, setRepoFilter] = useState('');
  const [iconFilter, setIconFilter] = useState<'' | 'with' | 'without'>('');
  const [storeUrlFilter, setStoreUrlFilter] = useState<'' | 'with' | 'without'>('');
  const [firebaseFilter, setFirebaseFilter] = useState<'' | 'with' | 'without'>('');
  // '' all, 'with' / 'without' any of our Play consoles, otherwise a play_accounts.id.
  const [consoleFilter, setConsoleFilter] = useState('');
  // ISO-2: apps open on Google Play in that country.
  const [playGeoFilter, setPlayGeoFilter] = useState('');
  const [commentsFilter, setCommentsFilter] = useState<'' | 'with' | 'without'>('');
  const [sortBy, setSortBy] = useState<'name_asc' | 'name_desc' | 'newest' | 'oldest' | 'platform'>('newest');
  const [showFilters, setShowFilters] = useState(false);
  const [page, setPage] = useState(1);

  // Query for fetching apps
  const {
    data: apps = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ['apps'],
    queryFn: getApps,
  });
  const { data: appOwners = [] } = useQuery({
    queryKey: ['users', 'app-owners'],
    queryFn: getAppOwners,
    enabled: isTechnical && modalMode === 'create',
  });

  // Query for fetching app campaigns (only when campaigns modal is open)
  const {
    data: appCampaigns = [],
    isLoading: isLoadingAppCampaigns,
  } = useQuery({
    queryKey: ['app-campaigns', selectedApp?.id],
    queryFn: () => getAppCampaigns(selectedApp!.id),
    enabled: modalMode === 'campaigns' && !!selectedApp,
  });

  // Query for app geo coverage (only when geos modal is open)
  const {
    data: appGeos,
    isLoading: isLoadingAppGeos,
    error: appGeosError,
  } = useQuery({
    queryKey: ['app-geos', selectedApp?.id],
    queryFn: () => getAppGeos(selectedApp!.id),
    enabled: modalMode === 'geos' && !!selectedApp,
  });

  // Query for all campaigns (for the dropdown)
  const {
    data: allCampaigns = [],
  } = useQuery({
    queryKey: ['campaigns'],
    queryFn: getCampaigns,
    enabled: modalMode === 'campaigns',
  });

  // Mutation for creating app
  const createMutation = useMutation({
    mutationFn: (data: CreateAppInput) => createApp(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['apps'] });
      closeModal();
    },
    onError: (err: Error) => {
      setFormError(err.message);
    },
  });

  // Mutation for updating app
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateAppInput }) =>
      updateApp(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['apps'] });
      queryClient.invalidateQueries({ queryKey: ['play-apps'] });
      closeModal();
    },
    onError: (err: Error) => {
      setFormError(err.message);
    },
  });

  // Mutation for toggling the "in work" flag inline from the table row,
  // without opening the edit modal.
  const inWorkMutation = useMutation({
    mutationFn: ({ id, in_work }: { id: string; in_work: boolean }) =>
      updateApp(id, { in_work }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['apps'] });
    },
  });

  // Forbidden geos edited right in the Geo coverage modal; the modal stays open.
  const [forbiddenDraft, setForbiddenDraft] = useState<string[] | null>(null);
  const forbiddenMutation = useMutation({
    mutationFn: ({ id, codes }: { id: string; codes: string[] }) => updateApp(id, { forbidden_countries: codes }),
    onSuccess: () => {
      setForbiddenDraft(null);
      queryClient.invalidateQueries({ queryKey: ['apps'] });
    },
  });

  // Mutation for deleting app
  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteApp(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['apps'] });
      closeModal();
    },
    onError: (err: Error) => {
      setFormError(err.message);
    },
  });

  // On-demand refresh of one app's Play review state. `variables` is the app id,
  // so the row that was clicked can show a spinner while it's pending.
  const reviewRefreshMutation = useMutation({
    mutationFn: (appId: string) => refreshAppReview(appId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['apps'] }),
  });

  const clearHoldMutation = useMutation({
    mutationFn: (appId: string) => clearOfferHold(appId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['apps'] }),
  });

  // Mutation for assigning campaigns
  const assignMutation = useMutation({
    mutationFn: ({ appId, campaignIds }: { appId: string; campaignIds: string[] }) =>
      assignCampaigns(appId, campaignIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['app-campaigns', selectedApp?.id] });
      setSelectedCampaignId('');
    },
    onError: (err: Error) => {
      setFormError(err.message);
    },
  });

  // Mutation for unassigning campaigns
  const unassignMutation = useMutation({
    mutationFn: ({ appId, campaignId }: { appId: string; campaignId: string }) =>
      unassignCampaign(appId, campaignId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['app-campaigns', selectedApp?.id] });
    },
    onError: (err: Error) => {
      setFormError(err.message);
    },
  });

  // Mutation for refreshing store versions
  const refreshMutation = useMutation({
    mutationFn: refreshStoreVersions,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['apps'] });
    },
  });

  const openCreateModal = () => {
    setFormData(initialFormData);
    setFormError('');
    setSelectedApp(null);
    setModalMode('create');
  };

  const openEditModal = (app: App) => {
    setFormData({
      owner_admin_id: app.owner_admin_id || '',
      name: app.name,
      platform: app.platform,
      bundle_id: app.bundle_id,
      store_url: app.store_url || '',
      force_offer_url: app.force_offer_url || '',
      force_offer_enabled: !!app.force_offer_enabled,
      skip_version_check: !!app.skip_version_check,
      antimoderation_off: !!app.antimoderation_off,
      antimoderation_off_countries: antimodCountries(app),
      forbidden_countries: forbiddenCountries(app),
      external_account: !!app.external_account,
      icon_url: app.icon_url || '',
      description: app.description || '',
      release_notes_keywords: app.release_notes_keywords || '',
      vertical: app.vertical || '',
      status: app.status as 'active' | 'paused',
      pause_reason: app.pause_reason || '',
      force_offer_off_reason: app.force_offer_off_reason || '',
      in_work: !!app.in_work,
    });
    setFormError('');
    setSelectedApp(app);
    setModalMode('edit');
  };

  const openDeleteModal = (app: App) => {
    setSelectedApp(app);
    setFormError('');
    setModalMode('delete');
  };

  const openGeosModal = (app: App) => {
    setSelectedApp(app);
    setFormError('');
    setModalMode('geos');
  };

  const openCampaignsModal = (app: App) => {
    setSelectedApp(app);
    setFormError('');
    setSelectedCampaignId('');
    setModalMode('campaigns');
  };

  const closeModal = () => {
    setModalMode(null);
    setSelectedApp(null);
    setFormData(initialFormData);
    setFormError('');
    setSelectedCampaignId('');
    setForbiddenDraft(null);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');

    if (!formData.name.trim()) {
      setFormError('Name is required');
      return;
    }

    if (!formData.bundle_id.trim()) {
      setFormError('Bundle ID is required');
      return;
    }

    if (modalMode === 'create') {
      if (isTechnical && !formData.owner_admin_id) {
        setFormError('ASO owner is required');
        return;
      }
      createMutation.mutate({
        name: formData.name.trim(),
        platform: formData.platform,
        bundle_id: formData.bundle_id.trim(),
        store_url: formData.store_url.trim() || null,
        icon_url: formData.icon_url.trim() || null,
        description: formData.description.trim() || null,
        release_notes_keywords: formData.release_notes_keywords.trim() || null,
        vertical: formData.vertical || null,
        owner_admin_id: formData.owner_admin_id || undefined,
      });
    } else if (modalMode === 'edit' && selectedApp) {
      // A reason is mandatory when the app is actually being turned off:
      // active -> paused, or force offer enabled -> disabled.
      const pausingNow = formData.status === 'paused' && selectedApp.status === 'active';
      if (pausingNow && !formData.pause_reason.trim()) {
        setFormError('Please enter a reason for pausing the app');
        return;
      }
      // Only a truly active force offer (was enabled AND had a URL) needs a
      // reason when turned off — a URL-less offer never served and has no
      // reason field, so requiring one would make the form unsubmittable.
      const disablingForceNow =
        !formData.force_offer_enabled && !!selectedApp.force_offer_enabled && !!selectedApp.force_offer_url;
      if (disablingForceNow && !formData.force_offer_off_reason.trim()) {
        setFormError('Please enter a reason for turning off the force offer');
        return;
      }
      updateMutation.mutate({
        id: selectedApp.id,
        data: {
          name: formData.name.trim(),
          platform: formData.platform,
          bundle_id: formData.bundle_id.trim(),
          store_url: formData.store_url.trim() || null,
          force_offer_url: formData.force_offer_url.trim() || null,
          force_offer_enabled: formData.force_offer_enabled,
          force_offer_off_reason: formData.force_offer_off_reason.trim() || null,
          skip_version_check: formData.skip_version_check,
          antimoderation_off: formData.antimoderation_off,
          antimoderation_off_countries: formData.antimoderation_off_countries,
          forbidden_countries: formData.forbidden_countries,
          external_account: formData.external_account,
          icon_url: formData.icon_url.trim() || null,
          description: formData.description.trim() || null,
          release_notes_keywords: formData.release_notes_keywords.trim() || null,
          vertical: formData.vertical || null,
          status: formData.status,
          pause_reason: formData.pause_reason.trim() || null,
          in_work: formData.in_work,
        },
      });
    }
  };

  const handleDelete = () => {
    if (selectedApp) {
      deleteMutation.mutate(selectedApp.id);
    }
  };

  const handleAssignCampaign = () => {
    if (selectedApp && selectedCampaignId) {
      assignMutation.mutate({ appId: selectedApp.id, campaignIds: [selectedCampaignId] });
    }
  };

  const handleUnassignCampaign = (campaignId: string) => {
    if (selectedApp) {
      unassignMutation.mutate({ appId: selectedApp.id, campaignId });
    }
  };

  const handleCopyApiKey = async (app: App, e: React.MouseEvent) => {
    e.stopPropagation();
    const success = await copyToClipboard(app.api_key);
    if (success) {
      setCopiedId(app.id);
      setTimeout(() => setCopiedId(null), 2000);
    }
  };

  const [copiedBundleId, setCopiedBundleId] = useState<string | null>(null);
  const handleCopyBundle = async (app: App, e: React.MouseEvent) => {
    e.stopPropagation();
    const success = await copyToClipboard(app.bundle_id);
    if (success) {
      setCopiedBundleId(app.id);
      setTimeout(() => setCopiedBundleId(null), 2000);
    }
  };

  const getAppStoreLink = (app: App): string => {
    if (app.platform === 'android') {
      return `https://play.google.com/store/apps/details?id=${app.bundle_id}`;
    }
    return `https://apps.apple.com/app/${app.bundle_id}`;
  };

  const [copiedLinkId, setCopiedLinkId] = useState<string | null>(null);
  const handleCopyLink = async (app: App, e: React.MouseEvent) => {
    e.stopPropagation();
    const success = await copyToClipboard(getAppStoreLink(app));
    if (success) {
      setCopiedLinkId(app.id);
      setTimeout(() => setCopiedLinkId(null), 2000);
    }
  };

  // Apply filters + sort
  const filteredApps = useMemo(() => {
    const q = searchFilter.trim().toLowerCase();
    let result = apps.filter((app) => {
      if (q) {
        const haystack = `${app.name} ${app.bundle_id} ${app.description || ''}`.toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      if (platformFilter && app.platform !== platformFilter) return false;
      if (verticalFilter === 'none' ? !!app.vertical : verticalFilter && app.vertical !== verticalFilter) return false;
      const ver = app.platform === 'ios' ? app.store_version_ios : app.store_version_android;
      if (statusFilter === 'banned') {
        if (!app.is_banned) return false;
      } else if (statusFilter === 'in_store') {
        // Live on the store: a store version was seen and no ban since.
        if (!ver || app.is_banned) return false;
      } else if (statusFilter === 'active') {
        // A banned app is logically not active even if its status is still
        // 'active', so keep it out of the Active filter.
        if (app.status !== 'active' || app.is_banned) return false;
      } else if (statusFilter && app.status !== statusFilter) return false;
      const forceOfferActive = !!(app.force_offer_enabled && app.force_offer_url);
      if (forceOfferFilter === 'on' && !forceOfferActive) return false;
      if (forceOfferFilter === 'off' && forceOfferActive) return false;
      if (versionFilter === 'with' && !ver) return false;
      if (versionFilter === 'without' && ver) return false;
      if (skipVersionFilter === 'on' && !app.skip_version_check) return false;
      if (skipVersionFilter === 'off' && app.skip_version_check) return false;
      // 'off' = выключена везде (antimoderation_off = 1), 'geo' = только в
      // отдельных гео, 'on' = включена везде.
      const amGeos = antimodCountries(app).length > 0;
      // Забаненное приложение считается 'off': бан выключает антимодерку сам.
      const amOffAll = !!app.antimoderation_off || !!app.is_banned;
      if (antimodFilter === 'off' && !amOffAll) return false;
      if (antimodFilter === 'geo' && (amOffAll || !amGeos)) return false;
      if (antimodFilter === 'on' && (amOffAll || amGeos)) return false;
      if (accountFilter === 'external' && !app.external_account) return false;
      if (accountFilter === 'ours' && app.external_account) return false;
      if (repoFilter === 'none' ? app.build_repo_id != null : repoFilter && String(app.build_repo_id ?? '') !== repoFilter) return false;
      if (iconFilter === 'with' && !app.icon_url) return false;
      if (iconFilter === 'without' && app.icon_url) return false;
      if (storeUrlFilter === 'with' && !app.store_url) return false;
      if (storeUrlFilter === 'without' && app.store_url) return false;
      if (firebaseFilter === 'with' && !app.firebase_project) return false;
      if (firebaseFilter === 'without' && app.firebase_project) return false;
      if (consoleFilter === 'with' && !app.play_account_id) return false;
      if (consoleFilter === 'without' && app.play_account_id) return false;
      if (consoleFilter && consoleFilter !== 'with' && consoleFilter !== 'without' && String(app.play_account_id ?? '') !== consoleFilter) return false;
      if (playGeoFilter && !(playCountries(app) ?? []).includes(playGeoFilter)) return false;
      if (commentsFilter === 'with' && !app.comments_count) return false;
      if (commentsFilter === 'without' && app.comments_count) return false;
      return true;
    });
    result = [...result].sort((a, b) => {
      switch (sortBy) {
        case 'name_asc':
          return a.name.localeCompare(b.name);
        case 'name_desc':
          return b.name.localeCompare(a.name);
        case 'oldest':
          return (a.created_at || '').localeCompare(b.created_at || '');
        case 'platform':
          return a.platform.localeCompare(b.platform) || a.name.localeCompare(b.name);
        case 'newest':
        default:
          return (b.created_at || '').localeCompare(a.created_at || '');
      }
    });
    return result;
  }, [apps, searchFilter, platformFilter, verticalFilter, statusFilter, forceOfferFilter, versionFilter, skipVersionFilter, antimodFilter, accountFilter, repoFilter, iconFilter, storeUrlFilter, firebaseFilter, consoleFilter, playGeoFilter, commentsFilter, sortBy]);

  const totalPages = Math.max(1, Math.ceil(filteredApps.length / APPS_PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const paginatedApps = filteredApps.slice(
    (currentPage - 1) * APPS_PAGE_SIZE,
    currentPage * APPS_PAGE_SIZE,
  );

  useEffect(() => {
    setPage(1);
  }, [searchFilter, platformFilter, verticalFilter, statusFilter, forceOfferFilter, versionFilter, skipVersionFilter, antimodFilter, accountFilter, repoFilter, iconFilter, storeUrlFilter, firebaseFilter, consoleFilter, playGeoFilter, commentsFilter, sortBy]);

  useEffect(() => {
    setPage((current) => Math.min(current, totalPages));
  }, [totalPages]);

  const activeFilterCount = [
    platformFilter,
    verticalFilter,
    statusFilter,
    forceOfferFilter,
    versionFilter,
    skipVersionFilter,
    antimodFilter,
    accountFilter,
    repoFilter,
    iconFilter,
    storeUrlFilter,
    firebaseFilter,
    consoleFilter,
    playGeoFilter,
    commentsFilter,
    searchFilter,
  ].filter(Boolean).length;

  // Only repos that actually built something in this list — the factory's
  // full registry lives on Apps Setup.
  const repoOptions = useMemo(() => {
    const seen = new Map<number, string>();
    for (const a of apps) if (a.build_repo_id != null && !seen.has(a.build_repo_id)) seen.set(a.build_repo_id, a.build_repo_name || `#${a.build_repo_id}`);
    const repos = Array.from(seen, ([id, name]) => ({ value: String(id), label: name })).sort((x, y) => x.label.localeCompare(y.label));
    return [{ value: '', label: 'All' }, { value: 'none', label: 'Без CI' }, ...repos];
  }, [apps]);

  // Consoles and Play countries that actually occur in the list.
  const consoleOptions = useMemo(() => {
    const seen = new Map<number, string>();
    for (const a of apps) if (a.play_account_id != null && !seen.has(a.play_account_id)) seen.set(a.play_account_id, a.play_account_name || `#${a.play_account_id}`);
    const list = Array.from(seen, ([id, name]) => ({ value: String(id), label: name })).sort((x, y) => x.label.localeCompare(y.label));
    return [{ value: '', label: 'All' }, { value: 'with', label: 'В нашей консоли' }, { value: 'without', label: 'Без консоли' }, ...list];
  }, [apps]);
  const playGeoOptions = useMemo(() => {
    const codes = new Set<string>();
    for (const a of apps) for (const c of playCountries(a) ?? []) codes.add(c);
    const list = [...codes]
      .map((c) => ({ value: c, label: `${COUNTRY_MAP[c]?.flag ?? ''} ${COUNTRY_MAP[c]?.name ?? c}`.trim() }))
      .sort((x, y) => x.label.localeCompare(y.label));
    return [{ value: '', label: 'All' }, ...list];
  }, [apps]);
  const commentsApp = commentsAppId ? apps.find((a) => a.id === commentsAppId) ?? null : null;
  const firebaseApp = firebaseAppId ? apps.find((a) => a.id === firebaseAppId) ?? null : null;

  const resetFilters = () => {
    setSearchInput('');
    setSearchFilter('');
    setPlatformFilter('');
    setVerticalFilter('');
    setStatusFilter('');
    setForceOfferFilter('');
    setVersionFilter('');
    setSkipVersionFilter('');
    setAntimodFilter('');
    setAccountFilter('');
    setRepoFilter('');
    setIconFilter('');
    setStoreUrlFilter('');
    setFirebaseFilter('');
    setConsoleFilter('');
    setPlayGeoFilter('');
    setCommentsFilter('');
  };

  const handleSearchSubmit = () => {
    setSearchFilter(searchInput.trim());
  };

  // Filter out already assigned campaigns for the dropdown
  const assignedCampaignIds = new Set(appCampaigns.map((ac: AppCampaign) => ac.id));
  const availableCampaigns = allCampaigns.filter((c: Campaign) => !assignedCampaignIds.has(c.id));

  // Keep related app data together instead of squeezing every field into a
  // separate column. The table remains readable at laptop widths and falls
  // back to horizontal scrolling on smaller screens.
  const cellBase = 'px-4 py-3 text-sm text-content align-middle';
  const headerBase = 'px-4';

  const columns = [
    {
      key: 'app',
      label: 'App',
      headerClassName: `${headerBase} w-[24%]`,
      cellClassName: `${cellBase} w-[24%]`,
      render: (app: App) => (
        <div className="flex items-center gap-3 min-w-0">
          <div
            className="w-10 h-10 rounded-lg overflow-hidden border border-border bg-surface-muted flex items-center justify-center shrink-0"
            style={{ aspectRatio: '1 / 1' }}
          >
            {app.icon_url ? (
              <img
                src={app.icon_url}
                alt={app.name}
                width={40}
                height={40}
                className="w-full h-full object-cover"
                style={{ aspectRatio: '1 / 1' }}
                loading="lazy"
                onError={(e) => {
                  (e.target as HTMLImageElement).style.visibility = 'hidden';
                }}
              />
            ) : (
              <svg className="w-4 h-4 text-content-subtle" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14M4 6h16v12H4V6z" />
              </svg>
            )}
          </div>
          <div className="min-w-0 space-y-0.5">
            <div className="flex items-center gap-2 min-w-0">
              <span className="font-medium text-content block truncate" title={app.name}>{app.name}</span>
              <span className="inline-flex shrink-0 items-center px-1.5 py-0.5 rounded text-[11px] font-medium bg-surface-muted text-content-muted">
                {app.platform === 'ios' ? 'iOS' : 'Android'}
              </span>
              {app.vertical && (
                <span className={`inline-flex shrink-0 items-center px-1.5 py-0.5 rounded text-[11px] font-medium ${VERTICAL_BADGE[app.vertical]}`}>
                  {verticalLabel(app.vertical)}
                </span>
              )}
              {!!app.external_account && (
                <span
                  className="inline-flex shrink-0 items-center px-1.5 py-0.5 rounded text-[11px] font-medium bg-violet-500/15 text-violet-500"
                  title={app.publisher_name ? `Сторонний аккаунт: ${app.publisher_name}` : 'Сторонний аккаунт (не наш Play / App Store)'}
                >
                  {app.publisher_name ? `Ext · ${app.publisher_name}` : 'Ext'}
                </span>
              )}
            </div>
            {app.description && (
              <p className="truncate text-xs text-content-subtle" title={app.description}>{app.description}</p>
            )}
            {!!app.comments_count && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setCommentsAppId(app.id); }}
                className="flex max-w-full items-center gap-1 text-left text-xs text-content-muted hover:text-primary"
                title={app.last_comment || ''}
              >
                <span className="shrink-0 rounded bg-surface-muted px-1 text-[10px] font-medium">💬 {app.comments_count}</span>
                <span className="truncate">{app.last_comment}</span>
              </button>
            )}
            <p className="truncate text-xs text-content-muted" title={app.owner_email || ''}>
              <span className="mr-1 text-[10px] uppercase tracking-wide text-content-subtle">ASO</span>
              {app.owner_name || app.owner_email || 'Unassigned'}
            </p>
          </div>
        </div>
      ),
    },
    {
      key: 'identity',
      label: 'Identity',
      headerClassName: `${headerBase} w-[20%]`,
      cellClassName: `${cellBase} w-[20%]`,
      render: (app: App) => (
        <div className="min-w-0 space-y-1.5">
          <div className="min-w-0">
            <span className="block text-[10px] uppercase tracking-wide text-content-subtle">Bundle ID</span>
            <div className="flex items-center gap-1 min-w-0">
              <span className="font-mono text-xs text-content-muted truncate" title={app.bundle_id}>
                {app.bundle_id}
              </span>
              <button
                type="button"
                onClick={(e) => handleCopyBundle(app, e)}
                className="p-1 text-content-subtle hover:text-primary transition-colors rounded hover:bg-surface-muted shrink-0"
                title="Copy bundle ID"
                aria-label={`Copy bundle ID of ${app.name}`}
              >
                {copiedBundleId === app.id ? (
                  <svg className="w-4 h-4 text-success" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                ) : (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                )}
              </button>
            </div>
          </div>
          {app.platform === 'android' && (
            <div className="flex items-center gap-1 min-w-0">
              <span className="text-[10px] uppercase tracking-wide text-content-subtle shrink-0">Console</span>
              {app.play_account_name ? (
                <span className="text-xs text-content-muted truncate" title={`Play console: ${app.play_account_name}`}>{app.play_account_name}</span>
              ) : (
                <span
                  className="text-xs text-content-subtle"
                  title={app.external_account ? 'Hosted in an outside console' : 'Not in any of our Play consoles'}
                >
                  {app.external_account ? 'external' : 'none'}
                </span>
              )}
            </div>
          )}
          <div className="flex items-center gap-1 min-w-0">
            <span className="text-[10px] uppercase tracking-wide text-content-subtle shrink-0">Firebase</span>
            {(() => {
              const node = app.firebase_provision_id ? (
                <span className={`text-xs ${app.firebase_provision_error ? 'text-danger' : 'text-warning'}`}>
                  {app.firebase_provision_error ? 'setup failed' : 'creating…'}
                </span>
              ) : app.firebase_project ? (
                <span className="font-mono text-xs text-success truncate" title={`Firebase project: ${app.firebase_project}`}>{app.firebase_project}</span>
              ) : (
                <span className="text-xs text-danger">none</span>
              );
              return isTechnical && app.platform === 'android' ? (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); setFirebaseAppId(app.id); }}
                  className="min-w-0 truncate rounded hover:bg-surface-muted px-0.5"
                  title={app.firebase_project ? 'Firebase: project and google-services.json' : 'Connect Firebase'}
                >
                  {node}
                </button>
              ) : node;
            })()}
          </div>
          {app.build_repo_name && (
            <div className="flex items-center gap-1 min-w-0">
              <span className="text-[10px] uppercase tracking-wide text-content-subtle shrink-0">Repo</span>
              <span className="text-xs text-content-muted truncate" title={`CI build source: ${app.build_repo_name}`}>{app.build_repo_name}</span>
            </div>
          )}
          <div className="flex items-center gap-1 min-w-0">
            <span className="text-[10px] uppercase tracking-wide text-content-subtle shrink-0">API</span>
            <span className="font-mono text-xs text-content-muted truncate">
              {app.api_key.slice(0, 6)}…{app.api_key.slice(-3)}
            </span>
            <button
              type="button"
              onClick={(e) => handleCopyApiKey(app, e)}
              className="p-1 text-content-subtle hover:text-primary transition-colors rounded hover:bg-surface-muted shrink-0"
              title="Copy API Key"
              aria-label={`Copy API key for ${app.name}`}
            >
              {copiedId === app.id ? (
                <svg className="w-4 h-4 text-success" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
              )}
            </button>
          </div>
        </div>
      ),
    },
    {
      key: 'store',
      label: 'Store',
      headerClassName: `${headerBase} w-[17%]`,
      cellClassName: `${cellBase} w-[17%]`,
      render: (app: App) => {
        const version = app.platform === 'ios'
          ? app.store_version_ios
          : app.store_version_android;
        const review = reviewBadge(app.review_state, app.review_state_since);
        const reviewNode = review
          ? <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${review.cls}`}>{review.text}</span>
          : <span className="text-content-subtle text-xs">No review data</span>;

        return (
          <div className="space-y-1.5">
            <PlayCountriesDropdown app={app} forbidden={forbiddenCountries(app)} />
            <div className="flex items-center gap-2 text-xs">
              <span className="text-content-subtle">Version</span>
              <span className={`font-mono ${version ? 'text-content' : 'text-content-subtle'}`}>
                {version || 'N/A'}
              </span>
            </div>
            <div className="flex items-center gap-1.5 min-w-0">
              {reviewNode}
              {app.platform === 'android' && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    reviewRefreshMutation.mutate(app.id);
                  }}
                  disabled={reviewRefreshMutation.isPending && reviewRefreshMutation.variables === app.id}
                  title="Refresh review status from Google Play"
                  aria-label={`Refresh review status for ${app.name}`}
                  className="p-1 rounded text-content-subtle hover:text-content hover:bg-surface-muted disabled:opacity-50 shrink-0"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2}
                    stroke="currentColor" className={`w-3.5 h-3.5 ${reviewRefreshMutation.isPending && reviewRefreshMutation.variables === app.id ? 'animate-spin' : ''}`}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992V4.356M2.985 19.644v-4.992h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99" />
                  </svg>
                </button>
              )}
            </div>
          </div>
        );
      },
    },
    {
      key: 'traffic',
      label: 'Traffic',
      headerClassName: `${headerBase} w-[14%]`,
      cellClassName: `${cellBase} w-[14%]`,
      render: (app: App) => (
        <div className="space-y-1.5 text-xs">
          <div className="flex items-center gap-2">
            <span className="w-12 shrink-0 text-content-subtle">Force</span>
            {app.force_offer_url ? (
              <span
                className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${app.force_offer_enabled ? 'bg-danger-soft text-danger' : 'bg-surface-muted text-content-subtle'}`}
                title={!app.force_offer_enabled && app.force_offer_off_reason ? app.force_offer_off_reason : undefined}
              >
                {app.force_offer_enabled ? 'ON' : 'OFF'}
              </span>
            ) : (
              <span className="text-content-subtle">Not set</span>
            )}
          </div>
          {app.force_offer_url && !!app.force_offer_enabled && app.offer_hold && (() => {
            const badge = offerHoldBadge(app.offer_hold);
            const busy = clearHoldMutation.isPending && clearHoldMutation.variables === app.id;
            return (
              <div className="flex items-center gap-1.5">
                <span className="w-12 shrink-0 text-content-subtle">Hold</span>
                <span
                  className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-warning-soft text-warning whitespace-nowrap"
                  title={badge.title}
                >
                  {badge.text}
                </span>
                {canEdit && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (window.confirm(`Включить force offer у «${app.name}» прямо сейчас, без паузы?`)) clearHoldMutation.mutate(app.id);
                    }}
                    disabled={busy}
                    title="Включить оффер сейчас, не дожидаясь конца паузы"
                    className="text-xs text-primary hover:underline disabled:opacity-50 whitespace-nowrap"
                  >
                    Turn on
                  </button>
                )}
              </div>
            );
          })()}
          <div className="flex items-center gap-2">
            <span className="w-12 shrink-0 text-content-subtle">Antimod</span>
            {app.antimoderation_off ? (
              <span
                className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-danger-soft text-danger"
                title="Антимодерка выключена: выдача идёт всем, включая забаненные девайсы"
              >
                OFF
              </span>
            ) : app.is_banned ? (
              <span
                className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-danger-soft text-danger"
                title="Приложение забанено — антимодерка выключена автоматически, все уходят на оффер. Снимут бан — вернётся сама"
              >
                OFF: ban
              </span>
            ) : antimodCountries(app).length > 0 ? (
              <span
                className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-warning-soft text-warning"
                title={`Антимодерка выключена в гео: ${antimodCountries(app).map((c) => COUNTRY_MAP[c]?.name || c).join(', ')}`}
              >
                OFF: {antimodGeoLabel(antimodCountries(app))}
              </span>
            ) : (
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-surface-muted text-content-subtle">
                ON
              </span>
            )}
          </div>
        </div>
      ),
    },
    {
      key: 'workflow',
      label: 'Workflow',
      headerClassName: `${headerBase} w-[13%]`,
      cellClassName: `${cellBase} w-[13%]`,
      render: (app: App) => {
        const inWork = !!app.in_work;
        const inWorkBusy = inWorkMutation.isPending && inWorkMutation.variables?.id === app.id;
        const statusNode = app.is_banned ? (
          <span
            className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-danger-soft text-danger"
            title={
              `Removed from store${app.banned_at ? ` since ${formatDateTime(app.banned_at)}` : ''}` +
              (app.ban_reason ? ` — ${app.ban_reason}` : '')
            }
          >
            Banned
          </span>
        ) : (
          <span
            className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${app.status === 'active' ? 'bg-success-soft text-success' : 'bg-surface-muted text-content-muted'}`}
            title={app.status !== 'active' && app.pause_reason ? app.pause_reason : undefined}
          >
            {app.status === 'active' ? 'Active' : 'Paused'}
          </span>
        );

        return (
          <div className="space-y-1.5 text-xs">
            <div className="flex items-center gap-2">
              <span className="w-12 shrink-0 text-content-subtle">Status</span>
              {statusNode}
            </div>
            <div className="flex items-center gap-2">
              <span className="w-12 shrink-0 text-content-subtle">In work</span>
              {canEdit ? (
                <button
                  type="button"
                  disabled={inWorkBusy}
                  onClick={(e) => {
                    e.stopPropagation();
                    inWorkMutation.mutate({ id: app.id, in_work: !inWork });
                  }}
                  title={inWork ? 'In work — click to turn off' : 'Not in work — click to turn on'}
                  aria-label={`${inWork ? 'Remove' : 'Add'} ${app.name} ${inWork ? 'from' : 'to'} work`}
                  className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:opacity-50 ${inWork ? 'bg-primary' : 'bg-surface-muted'}`}
                >
                  <span className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-surface shadow ring-0 transition duration-200 ease-in-out ${inWork ? 'translate-x-4' : 'translate-x-0'}`} />
                </button>
              ) : (
                <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${inWork ? 'bg-primary/10 text-primary' : 'bg-surface-muted text-content-subtle'}`}>
                  {inWork ? 'ON' : 'OFF'}
                </span>
              )}
            </div>
          </div>
        );
      },
    },
    {
      key: 'actions',
      label: '',
      headerClassName: `${headerBase} w-[12%]`,
      cellClassName: `${cellBase} w-[12%] whitespace-nowrap`,
      render: (app: App) => (
        <div className="flex flex-col items-end gap-1">
        <div className="flex items-center justify-end gap-0.5">
          <button
            type="button"
            onClick={(e) => handleCopyLink(app, e)}
            className="p-1.5 rounded text-content-subtle hover:text-primary hover:bg-surface-muted transition-colors"
            title="Copy app store link"
            aria-label={`Copy store link for ${app.name}`}
          >
            {copiedLinkId === app.id ? (
              <svg className="w-4 h-4 text-success" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            ) : (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
            )}
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setPackageApp(app);
            }}
            className="p-1.5 rounded text-content-subtle hover:text-primary hover:bg-surface-muted transition-colors"
            title="Store package: texts per locale, screenshots and live store changes"
            aria-label={`Store package of ${app.name}`}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </button>
          {canAsoMarket && app.platform === 'android' && (
            <Link
              to={`/aso-market?app=${encodeURIComponent(app.id)}`}
              onClick={(e) => e.stopPropagation()}
              className="p-1.5 rounded text-content-subtle hover:text-primary hover:bg-surface-muted transition-colors"
              title="Order ratings, reviews or installs on ASO.Market"
              aria-label={`ASO.Market order for ${app.name}`}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinejoin="round" strokeWidth={2} d="M12 3.5l2.6 5.3 5.9.9-4.3 4.1 1 5.8L12 16.9l-5.2 2.7 1-5.8-4.3-4.1 5.9-.9L12 3.5z" />
              </svg>
            </Link>
          )}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setCommentsAppId(app.id);
            }}
            className="relative p-1.5 rounded text-content-subtle hover:text-primary hover:bg-surface-muted transition-colors"
            title="Comments"
            aria-label={`Comments on ${app.name}`}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
            </svg>
            {!!app.comments_count && (
              <span className="absolute -top-0.5 -right-0.5 min-w-[14px] rounded-full bg-primary px-1 text-[9px] leading-[14px] text-white">{app.comments_count}</span>
            )}
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              openGeosModal(app);
            }}
            className="p-1.5 rounded text-content-subtle hover:text-primary hover:bg-surface-muted transition-colors"
            title="Geo coverage"
            aria-label={`Geo coverage for ${app.name}`}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              openCampaignsModal(app);
            }}
            className="p-1.5 rounded text-content-subtle hover:text-primary hover:bg-surface-muted transition-colors"
            title="Manage Campaigns"
            aria-label={`Manage campaigns for ${app.name}`}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
            </svg>
          </button>
          {canEdit && <>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                openEditModal(app);
              }}
              className="p-1.5 rounded text-content-subtle hover:text-content hover:bg-surface-muted transition-colors"
              title="Edit app"
              aria-label={`Edit ${app.name}`}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
            </button>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                openDeleteModal(app);
              }}
              className="p-1.5 rounded text-content-subtle hover:text-danger hover:bg-danger-soft transition-colors"
              title="Delete app"
              aria-label={`Delete ${app.name}`}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          </>}
        </div>
          <span className="text-xs text-content-subtle" title={`Created ${formatDateTime(app.created_at)}`}>
            Created {formatDate(app.created_at)}
          </span>
        </div>
      ),
    },
  ];

  // A build in review means the app is mid-update: turning the force offer or
  // the app itself back on is blocked (the API rejects it too) until the review
  // finishes. Only the "off -> on" direction is locked; an already-on control
  // stays editable.
  const inReview = modalMode === 'edit' && selectedApp?.review_state === 'IN_REVIEW';
  const statusLockedByReview = !!inReview && selectedApp?.status !== 'active';
  const forceLockedByReview = !!inReview && !selectedApp?.force_offer_enabled;

  if (error) {
    return (
      <div className="bg-danger-soft border border-danger/30 rounded-lg p-4">
        <p className="text-sm text-danger">
          Failed to load apps: {error instanceof Error ? error.message : 'Unknown error'}
        </p>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4 sm:mb-6">
        <div>
          <h1 className="text-xl sm:text-2xl font-semibold text-content">Apps</h1>
          <p className="text-sm text-content-subtle mt-1">
            Manage your mobile applications
          </p>
        </div>
        <Link
          to="/push/apps"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-content-muted border border-border-strong rounded-md hover:bg-surface-muted hover:text-content transition-colors"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
          </svg>
          Manage push
        </Link>
        {canEdit && <>
          <div className="flex items-center gap-3">
            <Button variant="secondary" onClick={() => setGoogleAdsOpen(true)}>
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
              </svg>
              Google Ads
            </Button>
            <Button
              variant="secondary"
              onClick={() => refreshMutation.mutate()}
              loading={refreshMutation.isPending}
            >
              <svg
                className={`w-4 h-4 mr-2 ${refreshMutation.isPending ? 'animate-spin' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
              Refresh Versions
            </Button>
            <Button onClick={openCreateModal}>
              <svg
                className="w-4 h-4 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4v16m8-8H4"
                />
              </svg>
              Add App
            </Button>
          </div>
        </>}
      </div>

      {/* Filters */}
      <div className="mb-4 space-y-3">
        <div className="flex items-end gap-3 flex-wrap">
          {/* Search */}
          <div className="flex-1 min-w-[220px] max-w-xs">
            <label htmlFor="apps-search" className="block text-sm font-medium text-content-muted mb-1">Search</label>
            <div className="flex">
              <input
                id="apps-search"
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleSearchSubmit(); }}
                placeholder="Name, bundle ID, description..."
                className="block w-full px-3 py-2 border border-border-strong rounded-l-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary sm:text-sm"
              />
              <button
                type="button"
                onClick={handleSearchSubmit}
                className="px-3 py-2 bg-surface-muted border border-l-0 border-border-strong rounded-r-md hover:bg-surface-muted text-content-muted"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </button>
            </div>
          </div>

          {/* Platform */}
          <div className="w-36">
            <label className="block text-sm font-medium text-content-muted mb-1">Platform</label>
            <Select
              value={platformFilter}
              onChange={(v) => setPlatformFilter(v as '' | 'ios' | 'android')}
              placeholder="All"
              options={[
                { value: '', label: 'All' },
                { value: 'android', label: 'Android' },
                { value: 'ios', label: 'iOS' },
              ]}
            />
          </div>

          {/* Vertical */}
          <div className="w-36">
            <label className="block text-sm font-medium text-content-muted mb-1">Vertical</label>
            <Select
              value={verticalFilter}
              onChange={(v) => setVerticalFilter(v as '' | 'none' | Vertical)}
              placeholder="All"
              options={[{ value: '', label: 'All' }, ...VERTICAL_OPTIONS, { value: 'none', label: 'Not set' }]}
            />
          </div>

          {/* Status */}
          <div className="w-32">
            <label className="block text-sm font-medium text-content-muted mb-1">Status</label>
            <Select
              value={statusFilter}
              onChange={(v) => setStatusFilter(v as typeof statusFilter)}
              placeholder="All"
              options={[
                { value: '', label: 'All' },
                { value: 'active', label: 'Active' },
                { value: 'paused', label: 'Paused' },
                { value: 'banned', label: 'Banned' },
                { value: 'in_store', label: 'In store' },
              ]}
            />
          </div>

          {/* Force offer */}
          <div className="w-36">
            <label className="block text-sm font-medium text-content-muted mb-1">Force Offer</label>
            <Select
              value={forceOfferFilter}
              onChange={(v) => setForceOfferFilter(v as '' | 'on' | 'off')}
              placeholder="All"
              options={[
                { value: '', label: 'All' },
                { value: 'on', label: 'On' },
                { value: 'off', label: 'Off' },
              ]}
            />
          </div>

          {/* Sort */}
          <div className="w-44">
            <label className="block text-sm font-medium text-content-muted mb-1">Sort by</label>
            <Select
              value={sortBy}
              onChange={(v) => setSortBy(v as typeof sortBy)}
              options={[
                { value: 'newest', label: 'Newest first' },
                { value: 'oldest', label: 'Oldest first' },
                { value: 'name_asc', label: 'Name (A→Z)' },
                { value: 'name_desc', label: 'Name (Z→A)' },
                { value: 'platform', label: 'Platform' },
              ]}
            />
          </div>

          {/* More filters toggle */}
          <button
            type="button"
            onClick={() => setShowFilters(!showFilters)}
            className={`px-3 py-2 text-sm border rounded-md transition-colors ${showFilters || activeFilterCount > 0 ? 'bg-primary/10 border-primary/30 text-primary' : 'border-border-strong text-content-muted hover:bg-surface-muted'}`}
          >
            <span className="flex items-center gap-1.5">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
              </svg>
              More
              {activeFilterCount > 0 && (
                <span className="inline-flex items-center justify-center w-5 h-5 text-xs font-medium bg-primary text-white rounded-full">
                  {activeFilterCount}
                </span>
              )}
            </span>
          </button>

          {activeFilterCount > 0 && (
            <button
              type="button"
              onClick={resetFilters}
              className="px-3 py-2 text-sm text-content-subtle hover:text-content-muted underline"
            >
              Reset
            </button>
          )}
        </div>

        {showFilters && (
          <div className="flex items-end gap-3 flex-wrap p-3 bg-surface-muted rounded-lg border border-border">
            {/* Store version */}
            <div className="w-44">
              <label className="block text-sm font-medium text-content-muted mb-1">Store Version</label>
              <Select
                value={versionFilter}
                onChange={(v) => setVersionFilter(v as '' | 'with' | 'without')}
                placeholder="All"
                options={[
                  { value: '', label: 'All' },
                  { value: 'with', label: 'Published' },
                  { value: 'without', label: 'Not published' },
                ]}
              />
            </div>

            {/* Skip version check */}
            <div className="w-44">
              <label className="block text-sm font-medium text-content-muted mb-1">Skip Version Check</label>
              <Select
                value={skipVersionFilter}
                onChange={(v) => setSkipVersionFilter(v as '' | 'on' | 'off')}
                placeholder="All"
                options={[
                  { value: '', label: 'All' },
                  { value: 'on', label: 'On' },
                  { value: 'off', label: 'Off' },
                ]}
              />
            </div>

            {/* Antimoderation */}
            <div className="w-44">
              <label className="block text-sm font-medium text-content-muted mb-1">Антимодерка</label>
              <Select
                value={antimodFilter}
                onChange={(v) => setAntimodFilter(v as '' | 'on' | 'off' | 'geo')}
                placeholder="All"
                options={[
                  { value: '', label: 'All' },
                  { value: 'on', label: 'Включена' },
                  { value: 'off', label: 'Выключена везде' },
                  { value: 'geo', label: 'Выключена в гео' },
                ]}
              />
            </div>

            {/* Store account */}
            <div className="w-44">
              <label className="block text-sm font-medium text-content-muted mb-1">Аккаунт</label>
              <Select
                value={accountFilter}
                onChange={(v) => setAccountFilter(v as '' | 'ours' | 'external')}
                placeholder="All"
                options={[
                  { value: '', label: 'All' },
                  { value: 'ours', label: 'Наш' },
                  { value: 'external', label: 'Сторонний' },
                ]}
              />
            </div>

            {/* CI repo */}
            <div className="w-52">
              <label className="block text-sm font-medium text-content-muted mb-1">Repo (CI)</label>
              <Select
                value={repoFilter}
                onChange={(v) => setRepoFilter(v)}
                placeholder="All"
                searchable
                options={repoOptions}
              />
            </div>

            {/* Icon */}
            <div className="w-40">
              <label className="block text-sm font-medium text-content-muted mb-1">Icon</label>
              <Select
                value={iconFilter}
                onChange={(v) => setIconFilter(v as '' | 'with' | 'without')}
                placeholder="All"
                options={[
                  { value: '', label: 'All' },
                  { value: 'with', label: 'With icon' },
                  { value: 'without', label: 'No icon' },
                ]}
              />
            </div>

            {/* Store URL */}
            <div className="w-44">
              <label className="block text-sm font-medium text-content-muted mb-1">Store URL</label>
              <Select
                value={storeUrlFilter}
                onChange={(v) => setStoreUrlFilter(v as '' | 'with' | 'without')}
                placeholder="All"
                options={[
                  { value: '', label: 'All' },
                  { value: 'with', label: 'Set' },
                  { value: 'without', label: 'Not set' },
                ]}
              />
            </div>

            {/* Firebase */}
            <div className="w-40">
              <label className="block text-sm font-medium text-content-muted mb-1">Firebase</label>
              <Select
                value={firebaseFilter}
                onChange={(v) => setFirebaseFilter(v as '' | 'with' | 'without')}
                placeholder="All"
                options={[
                  { value: '', label: 'All' },
                  { value: 'with', label: 'Есть' },
                  { value: 'without', label: 'Нет' },
                ]}
              />
            </div>

            {/* Play console */}
            <div className="w-52">
              <label className="block text-sm font-medium text-content-muted mb-1">Консоль</label>
              <Select
                value={consoleFilter}
                onChange={(v) => setConsoleFilter(v)}
                placeholder="All"
                searchable
                options={consoleOptions}
              />
            </div>

            {/* Countries open on Play */}
            <div className="w-52">
              <label className="block text-sm font-medium text-content-muted mb-1">Доступна в Play</label>
              <Select
                value={playGeoFilter}
                onChange={(v) => setPlayGeoFilter(v)}
                placeholder="All"
                searchable
                options={playGeoOptions}
              />
            </div>

            {/* Comments */}
            <div className="w-40">
              <label className="block text-sm font-medium text-content-muted mb-1">Комментарии</label>
              <Select
                value={commentsFilter}
                onChange={(v) => setCommentsFilter(v as '' | 'with' | 'without')}
                placeholder="All"
                options={[
                  { value: '', label: 'All' },
                  { value: 'with', label: 'Есть' },
                  { value: 'without', label: 'Нет' },
                ]}
              />
            </div>
          </div>
        )}

        <div className="text-xs text-content-subtle">
          Showing {paginatedApps.length} of {filteredApps.length} filtered apps ({apps.length} total)
        </div>
      </div>

      {/* Table */}
      <DataTable
        columns={columns}
        data={paginatedApps}
        loading={isLoading}
        onRowClick={canEdit ? openEditModal : undefined}
        keyExtractor={(app) => app.id}
        rowClassName={(app) =>
          // Keep inactive rows identifiable without overpowering their content.
          app.is_banned
            ? 'bg-danger-soft/20'
            : app.status !== 'active'
              ? 'bg-warning-soft/20'
              : undefined
        }
        fixedLayout
        minWidth="1100px"
      />

      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-3 px-4 py-3 bg-surface border border-border rounded-lg">
          <p className="text-sm text-content-subtle">
            Page {currentPage} of {totalPages}
          </p>
          <div className="flex gap-1">
            <button
              type="button"
              onClick={() => setPage((current) => Math.max(1, current - 1))}
              disabled={currentPage === 1}
              className="px-3 py-1 text-sm rounded border border-border disabled:opacity-40 hover:bg-surface-muted"
            >
              Prev
            </button>
            <button
              type="button"
              onClick={() => setPage((current) => Math.min(totalPages, current + 1))}
              disabled={currentPage === totalPages}
              className="px-3 py-1 text-sm rounded border border-border disabled:opacity-40 hover:bg-surface-muted"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Create/Edit Modal */}
      <Modal
        isOpen={modalMode === 'create' || modalMode === 'edit'}
        onClose={closeModal}
        title={modalMode === 'create' ? 'Add New App' : `Edit App — ${selectedApp?.name || ''}`}
        size={modalMode === 'edit' ? 'full' : 'md'}
      >
        <form onSubmit={handleSubmit} className={modalMode === 'edit' ? 'min-h-full flex flex-col' : 'space-y-4'}>
          {formError && (
            <div className="p-3 mb-4 bg-danger-soft border border-danger/30 rounded-md">
              <p className="text-sm text-danger">{formError}</p>
            </div>
          )}

          <div className={modalMode === 'edit' ? 'grid grid-cols-1 xl:grid-cols-12 gap-5 items-start flex-1' : 'space-y-4'}>
            <section className={modalMode === 'edit' ? 'rounded-xl border border-border bg-surface p-4 sm:p-5 space-y-4 xl:col-span-7' : 'space-y-4'}>
              {modalMode === 'edit' && <div>
                <h3 className="text-base font-semibold text-content">Identity & access</h3>
                <p className="mt-0.5 text-xs text-content-subtle">Main app details and SDK credentials.</p>
              </div>}

          {modalMode === 'create' && isTechnical && (
            <div>
              <label className="block text-sm font-medium text-content-muted">ASO owner</label>
              <Select
                value={formData.owner_admin_id}
                onChange={(v) => setFormData((prev) => ({ ...prev, owner_admin_id: v }))}
                options={[
                  { value: '', label: 'Select owner' },
                  ...appOwners
                    .map((user) => ({ value: user.id, label: user.name || user.email })),
                ]}
                className="mt-1 w-full"
              />
            </div>
          )}

          {/* API Key display for edit mode */}
          {modalMode === 'edit' && selectedApp && (
            <div>
              <label className="block text-sm font-medium text-content-muted mb-1">
                API Key
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={selectedApp.api_key}
                  readOnly
                  className="flex-1 px-3 py-2 border border-border-strong rounded-md bg-surface-muted font-mono text-sm text-content-muted select-all"
                  onClick={(e) => (e.target as HTMLInputElement).select()}
                />
                <button
                  type="button"
                  onClick={async () => {
                    const success = await copyToClipboard(selectedApp.api_key);
                    if (success) {
                      setCopiedId(selectedApp.id);
                      setTimeout(() => setCopiedId(null), 2000);
                    }
                  }}
                  className="px-3 py-2 border border-border-strong rounded-md bg-surface hover:bg-surface-muted transition-colors flex items-center gap-1 text-sm"
                >
                  {copiedId === selectedApp.id ? (
                    <>
                      <svg className="w-4 h-4 text-success" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      Copied
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                      Copy
                    </>
                  )}
                </button>
              </div>
              <p className="mt-1 text-xs text-content-subtle">
                Use this key to authenticate SDK requests from your app
              </p>
            </div>
          )}

          <div>
            <label
              htmlFor="name"
              className="block text-sm font-medium text-content-muted"
            >
              App Name
            </label>
            <input
              type="text"
              id="name"
              value={formData.name}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, name: e.target.value }))
              }
              className="mt-1 block w-full px-3 py-2 border border-border-strong rounded-md shadow-sm placeholder-content-subtle focus:outline-none focus:ring-primary focus:border-primary sm:text-sm"
              placeholder="My App"
            />
          </div>

          <div className={modalMode === 'edit' ? 'grid grid-cols-1 md:grid-cols-2 gap-4' : 'space-y-4'}>

          <div>
            <label
              htmlFor="description"
              className="block text-sm font-medium text-content-muted"
            >
              Description
              <span className="text-content-subtle font-normal ml-1">(optional)</span>
            </label>
            <textarea
              id="description"
              value={formData.description}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, description: e.target.value }))
              }
              rows={2}
              className="mt-1 block w-full px-3 py-2 border border-border-strong rounded-md shadow-sm placeholder-content-subtle focus:outline-none focus:ring-primary focus:border-primary sm:text-sm resize-none"
              placeholder="Short note about this app — vertical, geo, status, etc."
            />
          </div>

          <div>
            <label htmlFor="vertical" className="block text-sm font-medium text-content-muted">
              Vertical
              <span className="text-content-subtle font-normal ml-1">(what the app is built for)</span>
            </label>
            <Select
              id="vertical"
              value={formData.vertical}
              onChange={(v) => setFormData((prev) => ({ ...prev, vertical: v as '' | Vertical }))}
              options={[{ value: '', label: '—' }, ...VERTICAL_OPTIONS]}
              className="mt-1 w-full"
            />
          </div>

          <div>
            <label
              htmlFor="release_notes_keywords"
              className="block text-sm font-medium text-content-muted"
            >
              What&apos;s new keywords
              <span className="text-content-subtle font-normal ml-1">(optional)</span>
            </label>
            <input
              type="text"
              id="release_notes_keywords"
              value={formData.release_notes_keywords}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, release_notes_keywords: e.target.value }))
              }
              maxLength={1000}
              className="mt-1 block w-full px-3 py-2 border border-border-strong rounded-md shadow-sm placeholder-content-subtle focus:outline-none focus:ring-primary focus:border-primary sm:text-sm"
              placeholder="plinko, money"
            />
            <p className="mt-1 text-xs text-content-subtle">
              Up to 20 comma-separated themes used to turn rough build notes into natural, specific Google Play release copy.
            </p>
          </div>

          <div>
            <label
              htmlFor="platform"
              className="block text-sm font-medium text-content-muted"
            >
              Platform
            </label>
            <Select
              value={formData.platform}
              onChange={(v) =>
                setFormData((prev) => ({
                  ...prev,
                  platform: v as 'ios' | 'android',
                }))
              }
              className="mt-1"
              options={[
                { value: 'android', label: 'Android' },
                { value: 'ios', label: 'iOS' },
              ]}
            />
          </div>

          <div>
            <label
              htmlFor="bundle_id"
              className="block text-sm font-medium text-content-muted"
            >
              Bundle ID
            </label>
            <input
              type="text"
              id="bundle_id"
              value={formData.bundle_id}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, bundle_id: e.target.value }))
              }
              className="mt-1 block w-full px-3 py-2 border border-border-strong rounded-md shadow-sm placeholder-content-subtle focus:outline-none focus:ring-primary focus:border-primary sm:text-sm font-mono text-sm"
              placeholder="com.example.myapp"
            />
          </div>

          </div>
            </section>

            <section className={modalMode === 'edit' ? 'rounded-xl border border-border bg-surface p-4 sm:p-5 space-y-4 xl:col-span-5' : 'space-y-4'}>
              {modalMode === 'edit' && <div>
                <h3 className="text-base font-semibold text-content">Store listing</h3>
                <p className="mt-0.5 text-xs text-content-subtle">Icon and destination used across ads.</p>
              </div>}

          <div>
            <label className="block text-sm font-medium text-content-muted mb-1">
              App Icon
              <span className="text-content-subtle font-normal ml-1">
                (auto-pulled from store if available)
              </span>
            </label>
            <MediaUpload
              value={formData.icon_url}
              onChange={(url) => setFormData((prev) => ({ ...prev, icon_url: url }))}
              onUpload={(file) => uploadMedia(file)}
              accept="image/*"
              mediaType="image"
              variant="icon"
            />
            <p className="mt-1 text-xs text-content-subtle">
              Leave empty to auto-fetch from the App Store / Google Play using the bundle ID.
            </p>
          </div>

          <div>
            <label
              htmlFor="store_url"
              className="block text-sm font-medium text-content-muted"
            >
              Store URL
              <span className="text-content-subtle font-normal ml-1">(default for all ads)</span>
            </label>
            <input
              type="url"
              id="store_url"
              value={formData.store_url}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, store_url: e.target.value }))
              }
              className="mt-1 block w-full px-3 py-2 border border-border-strong rounded-md shadow-sm placeholder-content-subtle focus:outline-none focus:ring-primary focus:border-primary sm:text-sm"
              placeholder="https://play.google.com/store/apps/details?id=com.example.app"
            />
            <p className="mt-1 text-xs text-content-subtle">
              This URL will be used as the default Store URL for all ads in this app. Individual ads can override it.
            </p>
          </div>
            </section>

          {modalMode === 'edit' && (
            <section className="rounded-xl border border-border bg-surface p-4 sm:p-5 space-y-4 xl:col-span-6">
              <div>
                <h3 className="text-base font-semibold text-content">Traffic & workflow</h3>
                <p className="mt-0.5 text-xs text-content-subtle">Control forced traffic and Apps Setup visibility.</p>
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <span className="block text-sm font-medium text-content-muted">
                    Force Offer
                  </span>
                  <p className="text-xs text-content-subtle mt-0.5">
                    Turn the force offer on or off without losing the URL below
                  </p>
                  {forceLockedByReview && (
                    <p className="text-xs font-medium text-warning mt-0.5">
                      Приложение на обновлении (In review) — включить оффер нельзя
                    </p>
                  )}
                </div>
                <button
                  type="button"
                  disabled={forceLockedByReview}
                  title={forceLockedByReview ? 'Приложение на обновлении (In review) — оффер включить нельзя' : undefined}
                  onClick={() => setFormData((prev) => ({ ...prev, force_offer_enabled: !prev.force_offer_enabled }))}
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${
                    formData.force_offer_enabled ? 'bg-primary' : 'bg-surface-muted'
                  }`}
                >
                  <span
                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-surface shadow ring-0 transition duration-200 ease-in-out ${
                      formData.force_offer_enabled ? 'translate-x-5' : 'translate-x-0'
                    }`}
                  />
                </button>
              </div>
              <div className="flex items-center justify-between mt-4">
                <div>
                  <span className="block text-sm font-medium text-content-muted">
                    In work
                  </span>
                  <p className="text-xs text-content-subtle mt-0.5">
                    Hides the app from the AdMob linking list in Apps Setup
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setFormData((prev) => ({ ...prev, in_work: !prev.in_work }))}
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 ${
                    formData.in_work ? 'bg-primary' : 'bg-surface-muted'
                  }`}
                >
                  <span
                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-surface shadow ring-0 transition duration-200 ease-in-out ${
                      formData.in_work ? 'translate-x-5' : 'translate-x-0'
                    }`}
                  />
                </button>
              </div>
              <label
                htmlFor="force_offer_url"
                className="block text-sm font-medium text-content-muted mt-3"
              >
                Force Offer URL
              </label>
              <input
                type="text"
                id="force_offer_url"
                value={formData.force_offer_url}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, force_offer_url: e.target.value }))
                }
                className="mt-1 block w-full px-3 py-2 border border-border-strong rounded-md shadow-sm placeholder-content-subtle focus:outline-none focus:ring-primary focus:border-primary sm:text-sm font-mono text-sm"
                placeholder="https://example.com/offer?param=value"
              />
              <p className="mt-1 text-xs text-content-subtle">
                When the toggle is on and this URL is set, all users of this app will see a fullscreen webview with this URL on launch.
              </p>
              {formData.force_offer_enabled && formData.force_offer_url.trim() ? (
                <p className="mt-1 text-xs font-medium text-danger">
                  Force Offer is ACTIVE for this app
                </p>
              ) : formData.force_offer_url.trim() ? (
                <p className="mt-1 text-xs font-medium text-content-subtle">
                  Force Offer is OFF (URL kept, toggle disabled)
                </p>
              ) : null}
              {!formData.force_offer_enabled && (formData.force_offer_url.trim() || !!selectedApp?.force_offer_url) && (
                <div className="mt-3">
                  <label
                    htmlFor="force_offer_off_reason"
                    className="block text-sm font-medium text-content-muted"
                  >
                    Reason for turning off <span className="text-danger">*</span>
                  </label>
                  <textarea
                    id="force_offer_off_reason"
                    value={formData.force_offer_off_reason}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, force_offer_off_reason: e.target.value }))
                    }
                    rows={2}
                    className="mt-1 block w-full px-3 py-2 border border-border-strong rounded-md shadow-sm placeholder-content-subtle focus:outline-none focus:ring-primary focus:border-primary sm:text-sm resize-none"
                    placeholder="Why is the force offer being turned off?"
                  />
                  <p className="mt-1 text-xs text-content-subtle">
                    Shown in the apps list until the force offer is turned back on.
                  </p>
                </div>
              )}
            </section>
          )}

          <div className={modalMode === 'edit' ? 'rounded-xl border border-border bg-surface p-4 sm:p-5 space-y-5 xl:col-span-6' : 'hidden'}>
            <div>
              <h3 className="text-base font-semibold text-content">Availability & moderation</h3>
              <p className="mt-0.5 text-xs text-content-subtle">Publishing state and delivery safety controls.</p>
            </div>

          {modalMode === 'edit' && (
            <div className="flex items-center justify-between">
              <div>
                <span className="block text-sm font-medium text-content-muted">
                  Skip Version Check
                </span>
                <p className="text-xs text-content-subtle mt-0.5">
                  Allow ads even if the app is not yet published in the store
                </p>
              </div>
              <button
                type="button"
                onClick={() => setFormData((prev) => ({ ...prev, skip_version_check: !prev.skip_version_check }))}
                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 ${
                  formData.skip_version_check ? 'bg-primary' : 'bg-surface-muted'
                }`}
              >
                <span
                  className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-surface shadow ring-0 transition duration-200 ease-in-out ${
                    formData.skip_version_check ? 'translate-x-5' : 'translate-x-0'
                  }`}
                />
              </button>
            </div>
          )}

          {modalMode === 'edit' && (
            <div className="flex items-center justify-between">
              <div>
                <span className="block text-sm font-medium text-content-muted">
                  Антимодерка выключена
                </span>
                <p className="text-xs text-content-subtle mt-0.5">
                  Реклама и force offer уходят всем, включая забаненные девайсы.
                  Вердикты продолжают считаться и видны на странице Devices.
                  У забаненного приложения антимодерка выключается сама, пока бан открыт.
                </p>
                <p className="text-xs font-medium text-danger mt-0.5">
                  Если у приложения не заполнена store-версия (или не включён Skip
                  Version Check), устройства всё равно получат постоянный бан
                  review_visit, который действует во всех остальных приложениях.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setFormData((prev) => ({ ...prev, antimoderation_off: !prev.antimoderation_off }))}
                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 ${
                  formData.antimoderation_off ? 'bg-danger' : 'bg-surface-muted'
                }`}
              >
                <span
                  className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-surface shadow ring-0 transition duration-200 ease-in-out ${
                    formData.antimoderation_off ? 'translate-x-5' : 'translate-x-0'
                  }`}
                />
              </button>
            </div>
          )}

          {modalMode === 'edit' && !formData.antimoderation_off && (
            <div>
              <span className="block text-sm font-medium text-content-muted">
                Антимодерка выключена в гео
              </span>
              <p className="text-xs text-content-subtle mt-0.5 mb-2">
                То же, что тумблер выше, но только для запросов из этих стран (по IP).
                В остальных гео антимодерка работает как обычно.
              </p>
              <CountryMultiSelect
                value={formData.antimoderation_off_countries}
                onChange={(codes) => setFormData((prev) => ({ ...prev, antimoderation_off_countries: codes }))}
                placeholder="Выберите страны…"
                size="sm"
              />
            </div>
          )}

          {modalMode === 'edit' && (
            <div>
              <span className="block text-sm font-medium text-content-muted">
                ⛔ Запрещённые гео (нельзя лить)
              </span>
              <p className="text-xs text-content-subtle mt-0.5 mb-2">
                Бренд в этих странах недоступен. Только пометка: Geo Coverage показывает их значком
                и подсвечивает рекламу, которая туда таргетится. Выдачу не меняет.
              </p>
              <CountryMultiSelect
                value={formData.forbidden_countries}
                onChange={(codes) => setFormData((prev) => ({ ...prev, forbidden_countries: codes }))}
                placeholder="Выберите страны…"
                size="sm"
              />
            </div>
          )}

          {modalMode === 'edit' && (
            <div className="flex items-center justify-between">
              <div>
                <span className="block text-sm font-medium text-content-muted">
                  Сторонний аккаунт
                </span>
                <p className="text-xs text-content-subtle mt-0.5">
                  Прила лежит не в нашем Play / App Store аккаунте (публикатор, партнёр).
                  Ставится сама при назначении публикатора, снимать и ставить можно вручную.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setFormData((prev) => ({ ...prev, external_account: !prev.external_account }))}
                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 ${
                  formData.external_account ? 'bg-violet-500' : 'bg-surface-muted'
                }`}
              >
                <span
                  className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-surface shadow ring-0 transition duration-200 ease-in-out ${
                    formData.external_account ? 'translate-x-5' : 'translate-x-0'
                  }`}
                />
              </button>
            </div>
          )}

          {modalMode === 'edit' && selectedApp && (
            <div className="mb-4">
              <AppBanPanel appId={selectedApp.id} canEdit={canEdit} />
            </div>
          )}

          {modalMode === 'edit' && (
            <div>
              <label
                htmlFor="status"
                className="block text-sm font-medium text-content-muted"
              >
                Status
              </label>
              <Select
                value={formData.status || 'active'}
                onChange={(v) =>
                  setFormData((prev) => ({
                    ...prev,
                    status: v as 'active' | 'paused',
                  }))
                }
                className="mt-1"
                options={statusLockedByReview
                  ? [{ value: 'paused', label: 'Paused' }]
                  : [
                      { value: 'active', label: 'Active' },
                      { value: 'paused', label: 'Paused' },
                    ]}
              />
              {statusLockedByReview && (
                <p className="mt-1 text-xs font-medium text-warning">
                  Приложение на обновлении (In review) — статус Active поставить нельзя
                </p>
              )}
              {formData.status === 'paused' && (
                <div className="mt-3">
                  <label
                    htmlFor="pause_reason"
                    className="block text-sm font-medium text-content-muted"
                  >
                    Reason for pausing <span className="text-danger">*</span>
                  </label>
                  <textarea
                    id="pause_reason"
                    value={formData.pause_reason}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, pause_reason: e.target.value }))
                    }
                    rows={2}
                    className="mt-1 block w-full px-3 py-2 border border-border-strong rounded-md shadow-sm placeholder-content-subtle focus:outline-none focus:ring-primary focus:border-primary sm:text-sm resize-none"
                    placeholder="Why is this app being paused?"
                  />
                  <p className="mt-1 text-xs text-content-subtle">
                    Shown in the apps list until the app is set back to Active.
                  </p>
                </div>
              )}
            </div>
          )}

          </div>
          </div>

          <div className={modalMode === 'edit'
            ? 'sticky bottom-0 z-10 flex justify-end gap-3 mt-5 -mx-4 sm:-mx-6 -mb-4 px-4 sm:px-6 py-4 border-t border-border bg-surface/95 backdrop-blur'
            : 'flex justify-end gap-3 pt-4'}>
            <Button type="button" variant="secondary" onClick={closeModal}>
              Cancel
            </Button>
            <Button
              type="submit"
              loading={createMutation.isPending || updateMutation.isPending}
            >
              {modalMode === 'create' ? 'Create App' : 'Save Changes'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={modalMode === 'delete'}
        onClose={closeModal}
        title="Delete App"
        size="sm"
      >
        <div className="space-y-4">
          {formError && (
            <div className="p-3 bg-danger-soft border border-danger/30 rounded-md">
              <p className="text-sm text-danger">{formError}</p>
            </div>
          )}

          <p className="text-sm text-content-muted">
            Are you sure you want to delete{' '}
            <span className="font-medium text-content">
              {selectedApp?.name}
            </span>
            ? This action cannot be undone.
          </p>

          <div className="flex justify-end gap-3 pt-2">
            <Button type="button" variant="secondary" onClick={closeModal}>
              Cancel
            </Button>
            <Button
              type="button"
              variant="danger"
              onClick={handleDelete}
              loading={deleteMutation.isPending}
            >
              Delete
            </Button>
          </div>
        </div>
      </Modal>

      {/* Campaigns Management Modal */}
      <Modal
        isOpen={modalMode === 'campaigns'}
        onClose={closeModal}
        title={`Campaigns - ${selectedApp?.name || ''}`}
        size="lg"
      >
        <div className="space-y-4">
          {formError && (
            <div className="p-3 bg-danger-soft border border-danger/30 rounded-md">
              <p className="text-sm text-danger">{formError}</p>
            </div>
          )}

          {/* Add campaign */}
          {canEdit && <div>
            <label className="block text-sm font-medium text-content-muted mb-1">
              Assign Campaign
            </label>
            <div className="flex gap-2">
              <Select
                value={selectedCampaignId}
                onChange={(v) => setSelectedCampaignId(v)}
                placeholder="Select a campaign..."
                searchable
                className="flex-1"
                options={[
                  { value: '', label: 'Select a campaign...' },
                  ...availableCampaigns.map((campaign: Campaign) => ({
                    value: campaign.id,
                    label: `${campaign.name} (${campaign.status})`,
                  })),
                ]}
              />
              <Button
                type="button"
                onClick={handleAssignCampaign}
                disabled={!selectedCampaignId}
                loading={assignMutation.isPending}
              >
                Assign
              </Button>
            </div>
          </div>}

          {/* Assigned campaigns list */}
          <div>
            <label className="block text-sm font-medium text-content-muted mb-2">
              Assigned Campaigns
            </label>
            {isLoadingAppCampaigns ? (
              <div className="text-sm text-content-subtle py-4 text-center">Loading...</div>
            ) : appCampaigns.length === 0 ? (
              <div className="border border-dashed border-border-strong rounded-md p-4 text-center">
                <p className="text-sm text-content-subtle">No campaigns assigned</p>
                <p className="text-xs text-content-subtle mt-1">
                  This app will receive ads from all active campaigns
                </p>
              </div>
            ) : (
              <div className="border border-border rounded-md divide-y divide-border">
                {appCampaigns.map((campaign: AppCampaign) => (
                  <div key={campaign.id} className="flex items-center justify-between px-4 py-3">
                    <div className="flex items-center gap-3">
                      <span className="text-sm font-medium text-content">
                        {campaign.name}
                      </span>
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                          campaign.status === 'active'
                            ? 'bg-success-soft text-success'
                            : campaign.status === 'paused'
                            ? 'bg-warning-soft text-warning'
                            : 'bg-surface-muted text-content-muted'
                        }`}
                      >
                        {campaign.status}
                      </span>
                      {campaign.start_date && (
                        <span className="text-xs text-content-subtle">
                          {campaign.start_date}
                          {campaign.end_date ? ` - ${campaign.end_date}` : ''}
                        </span>
                      )}
                    </div>
                    {canEdit && <button
                      onClick={() => handleUnassignCampaign(campaign.id)}
                      className="text-content-subtle hover:text-danger transition-colors p-1"
                      title="Remove assignment"
                      disabled={unassignMutation.isPending}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>}
                  </div>
                ))}
              </div>
            )}
          </div>

          {appCampaigns.length > 0 && (
            <p className="text-xs text-content-subtle">
              Only ads from assigned campaigns will be shown in this app.
            </p>
          )}

          <div className="flex justify-end pt-2">
            <Button type="button" variant="secondary" onClick={closeModal}>
              Close
            </Button>
          </div>
        </div>
      </Modal>

      <GoogleAdsPublishModal isOpen={googleAdsOpen} onClose={() => setGoogleAdsOpen(false)} apps={apps} />

      <AppPackageModal app={packageApp} onClose={() => setPackageApp(null)} />
      <AppCommentsModal app={commentsApp} canWrite={canEdit} onClose={() => setCommentsAppId(null)} />
      <AppFirebaseModal app={firebaseApp} onClose={() => setFirebaseAppId(null)} />

      {/* Geo Coverage Modal */}
      <Modal
        isOpen={modalMode === 'geos'}
        onClose={closeModal}
        title={`Geo coverage — ${selectedApp?.name || ''}`}
        size="lg"
      >
        <div className="space-y-4">
          {appGeosError && (
            <div className="p-3 bg-danger-soft border border-danger/30 rounded-md">
              <p className="text-sm text-danger">
                {appGeosError instanceof Error ? appGeosError.message : 'Failed to load geos'}
              </p>
            </div>
          )}

          {isLoadingAppGeos ? (
            <div className="text-sm text-content-subtle py-4 text-center">Loading...</div>
          ) : appGeos ? (
            (() => {
              const runningSet = new Set(appGeos.running);
              const offeredSet = new Set(appGeos.offered);
              const forbiddenSet = new Set(forbiddenCountries(apps?.find((a) => a.id === selectedApp?.id) ?? selectedApp));
              const forbiddenRunning = [...forbiddenSet].filter((c) => runningSet.has(c));
              // Universe is the master country list shared with the Offers page
              // (so every ISO country shows up, including those without offers).
              const universe = COUNTRIES.map((c) => c.code);

              // "No offer" takes priority over running status — even if there's
              // an active ad, a geo with no offer means we can't monetize the
              // traffic and should be flagged as a problem.
              const noOfferCount = universe.filter((c) => !offeredSet.has(c)).length;
              const runningCount = universe.filter((c) => offeredSet.has(c) && runningSet.has(c)).length;
              const offerNoAdCount = universe.filter((c) => offeredSet.has(c) && !runningSet.has(c)).length;

              return (
                <>
                  <div className="flex flex-wrap gap-3 text-xs text-content-muted">
                    <span>Active ads: <span className="font-medium text-content">{appGeos.ads_total}</span></span>
                    <span>Without geo targeting: <span className="font-medium text-content">{appGeos.ads_untargeted}</span></span>
                  </div>

                  {canEdit && selectedApp && (
                    <div className="p-3 border border-border rounded-md space-y-2">
                      <div className="flex items-center justify-between gap-3">
                        <span className="text-xs font-medium text-content-muted">⛔ Forbidden geos — the brand is not available there</span>
                        {forbiddenDraft !== null && (
                          <span className="flex items-center gap-2">
                            <Button type="button" variant="secondary" size="sm" onClick={() => setForbiddenDraft(null)}>Cancel</Button>
                            <Button
                              type="button" size="sm" loading={forbiddenMutation.isPending}
                              onClick={() => forbiddenMutation.mutate({ id: selectedApp.id, codes: forbiddenDraft })}
                            >
                              Save
                            </Button>
                          </span>
                        )}
                      </div>
                      <CountryMultiSelect
                        value={forbiddenDraft ?? [...forbiddenSet]}
                        onChange={(codes) => setForbiddenDraft(codes)}
                        placeholder="Add a country…"
                        size="sm"
                      />
                      {forbiddenMutation.error && <p className="text-xs text-danger">{(forbiddenMutation.error as Error).message}</p>}
                    </div>
                  )}

                  {forbiddenRunning.length > 0 && (
                    <div className="p-3 bg-danger-soft border border-danger/30 rounded-md text-xs text-danger">
                      ⛔ Ads target forbidden geos: {forbiddenRunning.map((c) => COUNTRY_MAP[c]?.name || c).join(', ')} — the brand is not available there.
                    </div>
                  )}

                  {appGeos.ads_untargeted > 0 && (
                    <div className="p-3 bg-warning-soft border border-warning/30 rounded-md text-xs text-warning">
                      {appGeos.ads_untargeted} active ad{appGeos.ads_untargeted === 1 ? '' : 's'} ha{appGeos.ads_untargeted === 1 ? 's' : 've'} no geo targeting and serve every country — the colors below assume strict per-geo targeting.
                    </div>
                  )}

                  <div className="flex flex-wrap gap-3 text-xs">
                    <span className="inline-flex items-center gap-1.5">
                      <span className="inline-block w-3 h-3 rounded bg-success-soft border border-success/40" />
                      Running + offer ({runningCount})
                    </span>
                    <span className="inline-flex items-center gap-1.5">
                      <span className="inline-block w-3 h-3 rounded bg-warning-soft border border-warning/40" />
                      Offer, no ad in app ({offerNoAdCount})
                    </span>
                    <span className="inline-flex items-center gap-1.5">
                      <span className="inline-block w-3 h-3 rounded bg-danger-soft border border-danger/40" />
                      No offer ({noOfferCount})
                    </span>
                    <span className="inline-flex items-center gap-1.5">
                      <span aria-hidden>⛔</span>
                      Forbidden — do not buy traffic ({forbiddenSet.size})
                    </span>
                  </div>

                  <div className="flex flex-wrap gap-1.5 max-h-96 overflow-y-auto p-2 border border-border rounded-md">
                    {universe.map((code) => {
                      const isRunning = runningSet.has(code);
                      const hasOffer = offeredSet.has(code);
                      const name = COUNTRY_MAP[code]?.name || code;
                      // Forbidden outranks everything: the brand is not available there at all.
                      if (forbiddenSet.has(code)) {
                        return (
                          <span
                            key={code}
                            title={`${code} — ${name}\nForbidden geo — do not buy traffic${isRunning ? '\nAn active ad still targets it!' : ''}`}
                            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-mono font-medium border bg-surface-muted text-content-subtle line-through ${isRunning ? 'border-danger ring-1 ring-danger' : 'border-border'}`}
                          >
                            <span aria-hidden className="no-underline">⛔</span>
                            {code}
                          </span>
                        );
                      }
                      // Priority: no-offer is the most important signal.
                      // - red bold = no offer + ad is running (wasted traffic)
                      // - red      = no offer + no ad
                      // - green    = offer + ad running (good)
                      // - orange   = offer + no ad (opportunity)
                      const cls = !hasOffer
                        ? (isRunning
                            ? 'bg-danger-soft text-danger border-danger ring-1 ring-danger'
                            : 'bg-danger-soft text-danger border-danger/30')
                        : isRunning
                          ? 'bg-success-soft text-success border-success/30'
                          : 'bg-warning-soft text-warning border-warning/30';
                      const stateText = !hasOffer
                        ? (isRunning
                            ? 'No offer for this geo — ad is running but traffic cannot be monetized'
                            : 'No offer for this geo')
                        : isRunning
                          ? 'Active ad + offer available'
                          : 'Offer exists, no active ad in this app';
                      return (
                        <span
                          key={code}
                          title={`${code} — ${name}\n${stateText}`}
                          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-mono font-medium border ${cls}`}
                        >
                          <span aria-hidden>{COUNTRY_FLAG[code] || ''}</span>
                          {code}
                        </span>
                      );
                    })}
                  </div>
                </>
              );
            })()
          ) : null}

          <div className="flex justify-end pt-2">
            <Button type="button" variant="secondary" onClick={closeModal}>
              Close
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
